/**
 * Meaningful Outcome Client Analytics
 *
 * Tracks Losi outcome completion from browser-only interactions.
 *
 * Primary responsibilities:
 * - Send meaningful outcome events through the existing analytics route
 * - Use localStorage only as an anonymous/client fallback for first-outcome state
 * - Fail safely when analytics storage or network calls are unavailable
 *
 * Dependencies:
 * - Analytics track helper
 * - Meaningful outcome analytics contract
 */

import { getOrCreateSessionId, trackWithResponse } from '@/lib/analytics/track'
import {
  MeaningfulOutcomeEventNames,
  sanitizeMeaningfulOutcomeProperties,
  type MeaningfulOutcomeProperties
} from '@/lib/analytics/meaningful-outcome-contract'

const LOCAL_FIRST_OUTCOME_KEY = 'losi_has_completed_meaningful_outcome_v1'
export const MEANINGFUL_OUTCOME_COMPLETED_BROWSER_EVENT =
  'losi:meaningful-outcome-completed'

export interface MeaningfulOutcomeCompletedBrowserEventDetail {
  isFirstMeaningfulOutcome: boolean
  properties: MeaningfulOutcomeProperties
  sessionId: string
}

declare global {
  interface WindowEventMap {
    'losi:meaningful-outcome-completed': CustomEvent<MeaningfulOutcomeCompletedBrowserEventDetail>
  }
}

function readLocalFirstOutcomeState() {
  if (typeof window === 'undefined') return false

  try {
    return window.localStorage.getItem(LOCAL_FIRST_OUTCOME_KEY) === 'true'
  } catch {
    return false
  }
}

function persistLocalFirstOutcomeState() {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.setItem(LOCAL_FIRST_OUTCOME_KEY, 'true')
  } catch {
    // Analytics state should never block product actions.
  }
}

function readServerFirstOutcomeState(payload: Record<string, unknown> | null) {
  return typeof payload?.is_first_meaningful_outcome === 'boolean'
    ? payload.is_first_meaningful_outcome
    : null
}

function dispatchMeaningfulOutcomeCompleted(
  detail: MeaningfulOutcomeCompletedBrowserEventDetail
) {
  if (typeof window === 'undefined') return

  window.dispatchEvent(
    new CustomEvent(MEANINGFUL_OUTCOME_COMPLETED_BROWSER_EVENT, {
      detail
    })
  )
}

export async function trackMeaningfulOutcomeCompleted(
  properties: MeaningfulOutcomeProperties
) {
  try {
    const sanitized = sanitizeMeaningfulOutcomeProperties(properties)
    if (!sanitized) return

    const hasCompletedLocally = readLocalFirstOutcomeState()
    const sessionId = sanitized.session_id ?? getOrCreateSessionId()

    const eventProperties: MeaningfulOutcomeProperties = {
      ...sanitized,
      is_anonymous: sanitized.is_anonymous ?? true,
      is_first_meaningful_outcome:
        sanitized.is_first_meaningful_outcome ?? !hasCompletedLocally,
      session_id: sessionId
    }

    const tracked = await trackWithResponse(
      MeaningfulOutcomeEventNames.MEANINGFUL_OUTCOME_COMPLETED,
      eventProperties
    )

    if (!tracked.ok) {
      return
    }

    const serverFirstState = readServerFirstOutcomeState(tracked.payload)
    const isFirstMeaningfulOutcome =
      serverFirstState ?? (eventProperties.is_first_meaningful_outcome === true)

    dispatchMeaningfulOutcomeCompleted({
      isFirstMeaningfulOutcome,
      properties: {
        ...eventProperties,
        is_first_meaningful_outcome: isFirstMeaningfulOutcome
      },
      sessionId
    })

    if (!hasCompletedLocally) {
      persistLocalFirstOutcomeState()
    }
  } catch {
    // Analytics should never block product actions.
  }
}
